import { useCallback, useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { apiService } from '../../../services/api';
import {
  TAB_STATE,
  deleteDestination,
  homeTab,
  nextTabName,
  orderTabs,
  reorderedIds,
  resolveActiveTab,
  tabNameProblem,
} from './tabsUtils';

const LAST_TAB_KEY = 'insights_last_tab';

// The last tab a user had open, per user, so returning to Insights without a ?tab=
// lands where they left. A per-browser convenience: losing it costs nothing.
function readLastTab(userId) {
  try {
    return localStorage.getItem(`${LAST_TAB_KEY}:${userId}`);
  } catch {
    return null;
  }
}

function writeLastTab(userId, tabId) {
  try {
    localStorage.setItem(`${LAST_TAB_KEY}:${userId}`, tabId);
  } catch {
    /* storage disabled — the URL still carries the tab */
  }
}

/**
 * Tab state and actions for the Insights dashboard.
 *
 * Every action waits for the server before changing what is on screen, except a drag
 * reorder, which moves immediately and moves back if the save fails. Either way a
 * failure says so: nothing here fails silently.
 */
export function useDashboardTabs({ enabled, userId, notify, confirm }) {
  const [tabs, setTabs] = useState([]);
  const [canShare, setCanShare] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const [searchParams, setSearchParams] = useSearchParams();
  const requestedId = searchParams.get('tab') || readLastTab(userId);

  const activeTab = useMemo(() => resolveActiveTab(tabs, requestedId), [tabs, requestedId]);

  const fail = useCallback((err, fallback) => {
    console.error('[Insights] tab action failed:', err);
    notify?.(err?.message || fallback, 'error');
  }, [notify]);

  const load = useCallback(async () => {
    if (!enabled) return null;
    const res = await apiService.listDashboardTabs();
    setTabs(res.tabs || []);
    setCanShare(Boolean(res.can_share));
    setLoaded(true);
    return res.tabs || [];
  }, [enabled]);

  const selectTab = useCallback((tabId, { replace = false } = {}) => {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      next.set('tab', tabId);
      return next;
    }, { replace });
  }, [setSearchParams]);

  // Keep the URL and the remembered tab on the tab actually shown. A stale or hidden
  // id in the URL is replaced, not pushed, so Back does not return to a dead tab.
  useEffect(() => {
    if (!enabled || !activeTab) return;
    writeLastTab(userId, activeTab.id);
    if (searchParams.get('tab') !== activeTab.id) selectTab(activeTab.id, { replace: true });
  }, [enabled, activeTab, userId, searchParams, selectTab]);

  const createTab = useCallback(async () => {
    if (!enabled) return null;
    try {
      const tab = await apiService.createDashboardTab(nextTabName(tabs));
      setTabs((prev) => [...prev, tab]);
      selectTab(tab.id);
      return tab;
    } catch (err) {
      fail(err, 'Could not create the tab');
      return null;
    }
  }, [enabled, tabs, selectTab, fail]);

  // Returns true when saved. A name the server refuses leaves the old one on screen.
  const renameTab = useCallback(async (tabId, name) => {
    if (!enabled) return false;
    const problem = tabNameProblem(name, tabs, tabId);
    if (problem) {
      notify?.(problem, 'error');
      return false;
    }
    try {
      const updated = await apiService.updateDashboardTab(tabId, { name: name.trim() });
      setTabs((prev) => prev.map((t) => (t.id === tabId ? { ...t, ...updated } : t)));
      return true;
    } catch (err) {
      fail(err, 'Could not rename the tab');
      return false;
    }
  }, [enabled, tabs, notify, fail]);

  const setSharing = useCallback(async (tab, share, widgetCount) => {
    if (!enabled || !tab?.is_own) return false;
    if (share) {
      if (!canShare) return false;
      const ok = await confirm({
        title: 'Share tab',
        message:
          `Share "${tab.name}" with your organization? Everyone in your organization will see this tab `
          + `and all ${widgetCount} widget(s) on it, including private ones, laid out as you have them. `
          + 'Widgets you add to it later will be visible too.',
        confirmLabel: 'Share',
      });
      if (!ok) return false;
    }
    try {
      const updated = await apiService.updateDashboardTab(tab.id, { visibility: share ? 'organization' : 'private' });
      setTabs((prev) => prev.map((t) => (t.id === tab.id ? { ...t, ...updated } : t)));
      notify?.(share ? 'Tab shared with your organization' : 'Tab is private again', 'success');
      return true;
    } catch (err) {
      fail(err, 'Could not update sharing');
      return false;
    }
  }, [enabled, canShare, confirm, notify, fail]);

  // Resolves to true when the tab was deleted; the caller then reloads widgets, which
  // the server has already moved.
  const deleteTab = useCallback(async (tab, widgetCount) => {
    if (!enabled || !tab?.is_own) return false;
    const destination = deleteDestination(tabs, tab.id);
    if (!destination) return false;
    const where = widgetCount > 0
      ? ` Its ${widgetCount} widget(s) will move to "${destination.name}".`
      : '';
    const viewers = tab.state === TAB_STATE.SHARED_BY_YOU
      ? ' This tab is shared, so it will also disappear for everyone you shared it with.'
      : '';
    const ok = await confirm({
      title: 'Delete tab',
      message: `Delete "${tab.name}"?${where}${viewers} No widgets are deleted.`,
      confirmLabel: 'Delete',
      variant: 'danger',
    });
    if (!ok) return false;
    try {
      await apiService.deleteDashboardTab(tab.id);
      setTabs((prev) => prev.filter((t) => t.id !== tab.id));
      if (activeTab?.id === tab.id) selectTab(destination.id, { replace: true });
      return true;
    } catch (err) {
      fail(err, 'Could not delete the tab');
      return false;
    }
  }, [enabled, tabs, activeTab, confirm, selectTab, fail]);

  const moveTab = useCallback(async (tabId, toIndex) => {
    if (!enabled) return;
    const before = tabs;
    const ids = reorderedIds(tabs, tabId, toIndex);
    if (ids.join() === tabs.map((t) => t.id).join()) return;
    setTabs(orderTabs(tabs, ids));
    try {
      await apiService.reorderDashboardTabs(ids);
    } catch (err) {
      setTabs(before);
      fail(err, 'Could not save the tab order');
    }
  }, [enabled, tabs, fail]);

  const setHidden = useCallback(async (tab, hidden) => {
    if (!enabled || tab?.is_own) return;
    try {
      await apiService.setDashboardTabHidden(tab.id, hidden);
      setTabs((prev) => prev.map((t) => (t.id === tab.id ? { ...t, hidden } : t)));
      if (!hidden) selectTab(tab.id);
    } catch (err) {
      fail(err, hidden ? 'Could not hide the tab' : 'Could not show the tab');
    }
  }, [enabled, selectTab, fail]);

  // Share dashboard: every one of the user's tabs.
  const shareAll = useCallback(async () => {
    if (!enabled || !canShare) return;
    const mine = tabs.filter((t) => t.is_own && t.state !== TAB_STATE.SHARED_BY_YOU);
    if (mine.length === 0) {
      notify?.('All your tabs are already shared with your organization', 'info');
      return;
    }
    const ok = await confirm({
      title: 'Share dashboard',
      message:
        `Share all ${mine.length} of your unshared tab(s) with your organization? Everyone in your organization `
        + 'will see them and every widget on them, including private ones.',
      confirmLabel: 'Share',
    });
    if (!ok) return;
    try {
      await apiService.shareAllDashboardTabs();
      await load();
      notify?.('Dashboard shared with your organization', 'success');
    } catch (err) {
      fail(err, 'Failed to share dashboard');
    }
  }, [enabled, canShare, tabs, confirm, load, notify, fail]);

  // A user's own sizes for other people's widgets on one tab, as last saved. Set by
  // the dashboard when a resize is saved, and set back if that save fails.
  const setLayoutOverrides = useCallback((tabId, overrides) => {
    setTabs((prev) => prev.map((t) => (t.id === tabId ? { ...t, layout_overrides: overrides } : t)));
  }, []);

  return {
    tabs,
    canShare,
    loaded,
    activeTab,
    homeTab: homeTab(tabs),
    load,
    selectTab,
    createTab,
    renameTab,
    setSharing,
    deleteTab,
    moveTab,
    setHidden,
    shareAll,
    setLayoutOverrides,
  };
}

export default useDashboardTabs;
